import { strapiGet, strapiGetAll } from '@/lib/strapi'
import { db } from './database'
import type { Area } from '@/lib/types/area'
import type { Table } from '@/lib/types/table'
import type { OpeningHour } from '@/lib/types/openingHour'
import type { CustomField } from '@/lib/types/customField'
import type { Booking } from '@/lib/types/booking'
import type { CustomerOrder } from '@/lib/types/customerOrder'
import type { MenuCategory, MenuItem, BookingPreOrderItem, POSOrder } from '@/lib/types/pos'
import { normalizeMenuItems } from '@/lib/utils/pos'
import { format, subDays, addDays } from 'date-fns'

const BOOKING_POPULATE = {
  guest: true,
  bookingTables: { populate: { table: true, areaSnapshot: true } },
  bookingCustomFields: { populate: { customField: true } },
  openingHour: true,
}

/** Fetch a single booking + its pre-order items from the server and upsert into Dexie. */
export async function syncSingleBooking(documentId: string): Promise<void> {
  const resp = await strapiGet<Booking>(`/api/bookings/${documentId}`, {
    populate: BOOKING_POPULATE,
  })
  const booking = (resp as any).data ?? resp
  if (booking?.documentId) await db.bookings.put(booking)

  // Also sync pre-order items for this booking so CollectionsPage shows correct data immediately
  const poResp = await strapiGet<BookingPreOrderItem[]>('/api/booking-pre-order-items', {
    filters: { booking: { documentId: { $eq: documentId } } },
    populate: { menuItem: { populate: { category: true, variants: true } }, variant: true },
    pagination: { pageSize: 200 },
  })
  const items = ((poResp as any).data ?? poResp) as any[]
  if (items.length > 0) {
    const withKey = items.map((i) => ({ ...i, bookingDocumentId: documentId }))
    await db.preOrderItems.bulkPut(withKey)
  }
}

/** Fetch a single customer order + its pre-order items from the server and upsert into Dexie. */
export async function syncSingleCustomerOrder(documentId: string): Promise<void> {
  const resp = await strapiGet<CustomerOrder>(`/api/customer-orders/${documentId}`, {
    populate: {
      guest: true,
      openingHour: true,
      preOrderItems: { populate: { menuItem: { populate: { category: true, variants: true } }, variant: true } },
    },
  })
  const order = (resp as any).data ?? resp
  if (order?.documentId) await db.customerOrders.put(order)

  const poResp = await strapiGet<BookingPreOrderItem[]>('/api/booking-pre-order-items', {
    filters: { customerOrder: { documentId: { $eq: documentId } } },
    populate: { menuItem: { populate: { category: true, variants: true } }, variant: true },
    pagination: { pageSize: 200 },
  })
  const items = ((poResp as any).data ?? poResp) as any[]
  if (items.length > 0) {
    const withKey = items.map((i) => ({ ...i, orderDocumentId: documentId }))
    await db.preOrderItems.bulkPut(withKey)
  }
}

/** Fetch all bookings for a specific date from the server and upsert into Dexie. */
export async function syncBookingsForDate(restaurantId: string, date: string): Promise<void> {
  const resp = await strapiGet<Booking[]>('/api/bookings', {
    filters: {
      restaurant: { documentId: { $eq: restaurantId } },
      date: { $eq: date },
    },
    populate: BOOKING_POPULATE,
    pagination: { pageSize: 200 },
    sort: ['dateTime:asc'],
  })
  const serverBookings = (resp as any).data ?? resp
  const serverDocIds = new Set(serverBookings.map((b: Booking) => b.documentId))
  const staleKeys = await db.bookings
    .filter((b) =>
      b.date === date &&
      !(b.documentId ?? '').startsWith('temp-') &&
      !serverDocIds.has(b.documentId),
    )
    .primaryKeys()
  if (staleKeys.length > 0) await db.bookings.bulkDelete(staleKeys as string[])
  await db.bookings.bulkPut(serverBookings)
}

/** Fetch all customer orders for a specific date from the server and upsert into Dexie. */
export async function syncCustomerOrdersForDate(restaurantId: string, date: string): Promise<void> {
  const resp = await strapiGet<CustomerOrder[]>('/api/customer-orders', {
    filters: {
      restaurant: { documentId: { $eq: restaurantId } },
      date: { $eq: date },
    },
    populate: {
      guest: true,
      openingHour: true,
      preOrderItems: { populate: { menuItem: { populate: { category: true, variants: true } }, variant: true } },
    },
    pagination: { pageSize: 200 },
    sort: ['dateTime:asc'],
  })
  const serverOrders = (resp as any).data ?? resp
  const serverDocIds = new Set(serverOrders.map((o: CustomerOrder) => o.documentId))
  const staleKeys = await db.customerOrders
    .filter((o) =>
      o.date === date &&
      !(o.documentId ?? '').startsWith('temp-') &&
      !serverDocIds.has(o.documentId),
    )
    .primaryKeys()
  if (staleKeys.length > 0) await db.customerOrders.bulkDelete(staleKeys as string[])
  await db.customerOrders.bulkPut(serverOrders)
}

/**
 * Fetches all critical app data from the backend and writes it into Dexie.
 * Called on first login and on every reconnect.
 * `setProgress(0–100, label)` updates the loading bar.
 */
export async function runInitialSync(
  restaurantId: string,
  setProgress: (p: number, label: string) => void,
): Promise<void> {
  // ── Step 1: Areas ────────────────────────────────────────────────────── 0→20%
  setProgress(0, 'Syncing areas…')
  const areasResp = await strapiGet<Area[]>('/api/areas', {
    filters: { restaurant: { documentId: { $eq: restaurantId } } },
    populate: {
      tables: { filters: { deleted: { $eq: false } }, populate: '*' },
      tableCombinations: { populate: { tables: true } },
    },
    pagination: { pageSize: 100 },
    sort: ['name:asc'],
  })
  const serverAreas = ((areasResp as any).data ?? areasResp) as Area[]
  const serverAreaIds = new Set(serverAreas.map((a) => a.documentId))
  const staleAreaKeys = await db.areas
    .filter((a) => !serverAreaIds.has(a.documentId))
    .primaryKeys()
  if (staleAreaKeys.length > 0) await db.areas.bulkDelete(staleAreaKeys as string[])
  await db.areas.bulkPut(serverAreas)
  setProgress(20, 'Syncing tables…')

  // ── Step 2: Tables ──────────────────────────────────────────────────── 20→40%
  const tablesResp = await strapiGet<Table[]>('/api/tables', {
    filters: {
      deleted: { $eq: false },
      area: { restaurant: { documentId: { $eq: restaurantId } } },
    },
    populate: { area: true, tableCombinations: { populate: { tables: true } } },
    pagination: { pageSize: 200 },
    sort: ['name:asc'],
  })
  const serverTables = ((tablesResp as any).data ?? tablesResp) as Table[]
  const serverTableIds = new Set(serverTables.map((t) => t.documentId))
  const staleTableKeys = await db.floorTables
    .filter((t) => !serverTableIds.has(t.documentId))
    .primaryKeys()
  if (staleTableKeys.length > 0) await db.floorTables.bulkDelete(staleTableKeys as string[])
  await db.floorTables.bulkPut(serverTables)
  setProgress(40, 'Syncing opening hours…')

  // ── Step 3: Opening hours ───────────────────────────────────────────── 40→60%
  const ohResp = await strapiGet<OpeningHour[]>('/api/opening-hours', {
    filters: { restaurant: { documentId: { $eq: restaurantId } } },
    populate: { areas: true },
    pagination: { pageSize: 100 },
    sort: ['day:asc', 'open:asc'],
  })
  const serverOpeningHours = ((ohResp as any).data ?? ohResp) as OpeningHour[]
  const serverOpeningHourIds = new Set(serverOpeningHours.map((h) => h.documentId))
  const staleOpeningHourKeys = await db.openingHours
    .filter((h) => !serverOpeningHourIds.has(h.documentId))
    .primaryKeys()
  if (staleOpeningHourKeys.length > 0) await db.openingHours.bulkDelete(staleOpeningHourKeys as string[])
  await db.openingHours.bulkPut(serverOpeningHours)
  setProgress(60, 'Syncing custom fields…')

  // ── Step 4: Custom fields ───────────────────────────────────────────── 60→70%
  const cfResp = await strapiGet<CustomField[]>('/api/custom-fields', {
    filters: { restaurant: { documentId: { $eq: restaurantId } } },
    populate: { choiceOptions: true, openingHours: true },
    pagination: { pageSize: 100 },
    sort: ['sortIndex:asc'],
  })
  const serverCustomFields = ((cfResp as any).data ?? cfResp) as CustomField[]
  const serverCustomFieldIds = new Set(serverCustomFields.map((f) => f.documentId))
  const staleCustomFieldKeys = await db.customFields
    .filter((f) => !serverCustomFieldIds.has(f.documentId))
    .primaryKeys()
  if (staleCustomFieldKeys.length > 0) await db.customFields.bulkDelete(staleCustomFieldKeys as string[])
  await db.customFields.bulkPut(serverCustomFields)
  setProgress(70, 'Syncing bookings…')

  // ── Step 5: Menu categories ─────────────────────────────────────────── 70→80%
  setProgress(70, 'Syncing menu…')
  const categories = await strapiGetAll<MenuCategory>('/api/menu-categories', {
    filters: { restaurant: { documentId: { $eq: restaurantId } } },
    sort: ['sortOrder:asc', 'name:asc'],
    pagination: { pageSize: 100 },
  })
  await db.menuCategories.clear()
  await db.menuCategories.bulkPut(categories)

  // ── Step 6: Menu items ──────────────────────────────────────────────── 80→90%
  setProgress(80, 'Syncing menu items…')
  const items = await strapiGetAll<MenuItem>('/api/menu-items', {
    filters: { restaurant: { documentId: { $eq: restaurantId } }, deleted: { $ne: true } },
    populate: {
      category: true,
      variants: true,
      modifiers: true,
      restaurant: { fields: ['timezoneId'] },
    },
    sort: ['sortOrder:asc', 'name:asc'],
    pagination: { pageSize: 100 },
  })
  await db.menuItems.clear()
  await db.menuItems.bulkPut(normalizeMenuItems(items))
  setProgress(90, 'Syncing bookings…')

  // ── Step 7: Bookings ±30/+60 days ──────────────────────────────────── 90→97%
  const dateFrom = format(subDays(new Date(), 30), 'yyyy-MM-dd')
  const dateTo   = format(addDays(new Date(), 60), 'yyyy-MM-dd')
  const bookingsResp = await strapiGet<Booking[]>('/api/bookings', {
    filters: {
      restaurant: { documentId: { $eq: restaurantId } },
      date: { $gte: dateFrom, $lte: dateTo },
    },
    populate: {
      guest: true,
      bookingTables: { populate: { table: true, areaSnapshot: true } },
      bookingCustomFields: { populate: { customField: true } },
      openingHour: true,
    },
    pagination: { pageSize: 500 },
    sort: ['dateTime:asc'],
  })
  const serverBookings = (bookingsResp as any).data ?? bookingsResp
  // Preserve temp bookings (created offline, not yet synced) across re-sync
  const tempBookings = await db.bookings.filter((b) => (b.documentId ?? '').startsWith('temp-')).toArray()
  // bulkPut merges by primary key — server records overwrite existing, temps stay untouched
  const serverBookingIds = new Set(serverBookings.map((b: Booking) => b.documentId))
  const staleBookingKeys = await db.bookings
    .filter((b) =>
      b.date >= dateFrom &&
      b.date <= dateTo &&
      !(b.documentId ?? '').startsWith('temp-') &&
      !serverBookingIds.has(b.documentId),
    )
    .primaryKeys()
  if (staleBookingKeys.length > 0) await db.bookings.bulkDelete(staleBookingKeys as string[])
  await db.bookings.bulkPut([...serverBookings, ...tempBookings])
  const customerOrdersResp = await strapiGet<CustomerOrder[]>('/api/customer-orders', {
    filters: {
      restaurant: { documentId: { $eq: restaurantId } },
      date: { $gte: dateFrom, $lte: dateTo },
    },
    populate: {
      guest: true,
      openingHour: true,
      preOrderItems: { populate: { menuItem: { populate: { category: true, variants: true } }, variant: true } },
    },
    pagination: { pageSize: 500 },
    sort: ['dateTime:asc'],
  })
  const serverCustomerOrders = (customerOrdersResp as any).data ?? customerOrdersResp
  const serverCustomerOrderIds = new Set(serverCustomerOrders.map((o: CustomerOrder) => o.documentId))
  const staleCustomerOrderKeys = await db.customerOrders
    .filter((o) =>
      o.date >= dateFrom &&
      o.date <= dateTo &&
      !(o.documentId ?? '').startsWith('temp-') &&
      !serverCustomerOrderIds.has(o.documentId),
    )
    .primaryKeys()
  if (staleCustomerOrderKeys.length > 0) await db.customerOrders.bulkDelete(staleCustomerOrderKeys as string[])
  await db.customerOrders.bulkPut(serverCustomerOrders)
  setProgress(97, 'Syncing pre-orders…')

  // ── Step 8: Pre-order items for the same booking window ────────────── 97→100%
  const preOrderResp = await strapiGet<BookingPreOrderItem[]>('/api/booking-pre-order-items', {
    filters: {
      booking: {
        restaurant: { documentId: { $eq: restaurantId } },
        date: { $gte: dateFrom, $lte: dateTo },
      },
    },
    populate: { booking: true, menuItem: { populate: { category: true, variants: true, modifiers: true } }, variant: true },
    pagination: { pageSize: 2000 },
  })
  const orderPreOrderResp = await strapiGet<BookingPreOrderItem[]>('/api/booking-pre-order-items', {
    filters: {
      customerOrder: {
        restaurant: { documentId: { $eq: restaurantId } },
        date: { $gte: dateFrom, $lte: dateTo },
      },
    },
    populate: { customerOrder: true, menuItem: { populate: { category: true, variants: true, modifiers: true } }, variant: true },
    pagination: { pageSize: 2000 },
  })
  const preOrderItems = [
    ...(((preOrderResp as any).data ?? preOrderResp) as any[]),
    ...(((orderPreOrderResp as any).data ?? orderPreOrderResp) as any[]),
  ]
  const serverPreOrderItems = preOrderItems.map((item) => ({
    ...item,
    bookingDocumentId: item.booking?.documentId ?? '',
    orderDocumentId: item.customerOrder?.documentId ?? '',
  }))
  // Delete only server-known records; temp records (created offline, not yet synced) are preserved
  const nonTempPreOrderKeys = await db.preOrderItems
    .filter((i) => !(i.documentId ?? '').startsWith('temp-'))
    .primaryKeys()
  await db.preOrderItems.bulkDelete(nonTempPreOrderKeys as string[])
  await db.preOrderItems.bulkPut(serverPreOrderItems)
  setProgress(99, 'Syncing POS orders…')

  // ── Step 9: POS Orders (yesterday + today) ─────────────────────── 99→100%
  const posFrom = format(subDays(new Date(), 1), 'yyyy-MM-dd')
  const posTo   = format(new Date(), 'yyyy-MM-dd')
  const posOrdersResp = await strapiGet<POSOrder[]>('/api/pos/orders', {
    filters: {
      restaurant: { documentId: { $eq: restaurantId } },
      openedAt: {
        $gte: new Date(`${posFrom}T00:00:00.000`).toISOString(),
        $lte: new Date(`${posTo}T23:59:59.999`).toISOString(),
      },
    },
    populate: {
      table: true,
      orderItems: { populate: { menuItem: true, variant: true } },
      payments: true,
      booking: true,
    },
    sort: ['openedAt:desc'],
    pagination: { pageSize: 200 },
  })
  const serverPosOrders = ((posOrdersResp as any).data ?? posOrdersResp) as POSOrder[]
  // Preserve temp orders (not in server window) and temp items/payments inside real orders
  const tempPosOrders = await db.posOrders.filter((o) => (o.documentId ?? '').startsWith('temp-')).toArray()
  const serverDocIds = serverPosOrders.map((o) => o.documentId)
  const existingOrders = await db.posOrders.where('documentId').anyOf(serverDocIds).toArray()
  const existingByDocId = new Map(existingOrders.map((o) => [o.documentId, o]))
  const mappedPosOrders = serverPosOrders.map((o) => {
    const existing = existingByDocId.get(o.documentId)
    const tempItems    = existing?.orderItems?.filter((i) => i.documentId.startsWith('temp-')) ?? []
    const serverPaymentKeys = new Set((o.payments ?? []).map((p) => p.clientRequestId ?? p.bookosId))
    const tempPayments = existing?.payments?.filter((p) =>
      p.documentId.startsWith('temp-') && !serverPaymentKeys.has(p.clientRequestId ?? p.bookosId),
    ) ?? []
    return {
      ...o,
      tableDocId: o.table?.documentId ?? '',
      orderItems: [...(o.orderItems ?? []), ...tempItems],
      payments:   [...(o.payments   ?? []), ...tempPayments],
    }
  })
  const serverPosOrderIds = new Set(serverPosOrders.map((o) => o.documentId))
  const stalePosOrderKeys = await db.posOrders
    .filter((o) => {
      const openedDate = format(new Date(o.openedAt), 'yyyy-MM-dd')
      return openedDate >= posFrom &&
        openedDate <= posTo &&
        !(o.documentId ?? '').startsWith('temp-') &&
        !serverPosOrderIds.has(o.documentId)
    })
    .primaryKeys()
  if (stalePosOrderKeys.length > 0) await db.posOrders.bulkDelete(stalePosOrderKeys as string[])
  await db.posOrders.bulkPut([...mappedPosOrders, ...tempPosOrders])
  setProgress(100, 'Ready')
}
